export const name = "LEAD";
export const summary = `Gets a value for a subsequent row.`;
export const reference = `
## LEAD

Gets a value for a subsequent row.

\`\`\`
LEAD (value_expression[, offset [, default_expression]])
OVER over_clause

over_clause:
  { named_window | ( [window_specification] ) }

window_specification:
  [named_window]
  [PARTITION BY partition_expression [, ...] ]
  ORDER BY expression [{ ASC | DESC }] [, ...]
\`\`\`

### Description

Returns the value of the \`value_expression\` on a subsequent row. Changing the\`offset\` value changes which subsequent row is returned; the default value is\`1\`, indicating the next row in the window frame. An error occurs if \`offset\` is NULL or a negative value.

The optional \`default_expression\` is used if there isn't a row in the window frame at the specified offset. This expression must be a constant expression and its type must be implicitly coercible to the type of \`value_expression\`. If left unspecified, \`default_expression\` defaults to NULL.

To learn more about the \`OVER\` clause and how to use it, see Window function calls.


### Supported Argument Types

- \`value_expression\` can be any data type that can be returned from an expression.
- \`offset\` must be a non-negative integer literal or parameter.
- \`default_expression\` must be compatible with the value expression type.


### Return Data Type

Same type as \`value_expression\`.


### Examples

The following example illustrates a basic use of the \`LEAD\` function.



\`\`\`
WITH finishers AS
 (SELECT 'Sophia Liu' as name,
  TIMESTAMP '2016-10-18 2:51:45' as finish_time,
  'F30-34' as division
  UNION ALL SELECT 'Lisa Stelzner', TIMESTAMP '2016-10-18 2:54:11', 'F35-39'
  UNION ALL SELECT 'Nikki Leith', TIMESTAMP '2016-10-18 2:59:01', 'F30-34'
  UNION ALL SELECT 'Lauren Matthews', TIMESTAMP '2016-10-18 3:01:17', 'F35-39'
  UNION ALL SELECT 'Desiree Berry', TIMESTAMP '2016-10-18 3:05:42', 'F35-39'
  UNION ALL SELECT 'Suzy Slane', TIMESTAMP '2016-10-18 3:06:24', 'F35-39'
  UNION ALL SELECT 'Jen Edwards', TIMESTAMP '2016-10-18 3:06:36', 'F30-34'
  UNION ALL SELECT 'Meghan Lederer', TIMESTAMP '2016-10-18 3:07:41', 'F30-34'
  UNION ALL SELECT 'Carly Forte', TIMESTAMP '2016-10-18 3:08:58', 'F25-29'
  UNION ALL SELECT 'Lauren Reasoner', TIMESTAMP '2016-10-18 3:10:14', 'F30-34')
SELECT name,
  finish_time,
  division,
  LEAD(name)
    OVER (PARTITION BY division ORDER BY finish_time ASC) AS followed_by
FROM finishers;

/*-----------------+-------------+----------+-----------------*
 | name | finish_time | division | followed_by |
 +-----------------+-------------+----------+-----------------+
 | Carly Forte | 03:08:58 | F25-29 | NULL |
 | Sophia Liu | 02:51:45 | F30-34 | Nikki Leith |
 | Nikki Leith | 02:59:01 | F30-34 | Jen Edwards |
 | Jen Edwards | 03:06:36 | F30-34 | Meghan Lederer |
 | Meghan Lederer | 03:07:41 | F30-34 | Lauren Reasoner |
 | Lauren Reasoner | 03:10:14 | F30-34 | NULL |
 | Lisa Stelzner | 02:54:11 | F35-39 | Lauren Matthews |
 | Lauren Matthews | 03:01:17 | F35-39 | Desiree Berry |
 | Desiree Berry | 03:05:42 | F35-39 | Suzy Slane |
 | Suzy Slane | 03:06:24 | F35-39 | NULL |
 *-----------------+-------------+----------+-----------------*/
\`\`\`

This next example uses the optional \`offset\` parameter.



\`\`\`
WITH finishers AS
 (SELECT 'Sophia Liu' as name,
  TIMESTAMP '2016-10-18 2:51:45' as finish_time,
  'F30-34' as division
  UNION ALL SELECT 'Lisa Stelzner', TIMESTAMP '2016-10-18 2:54:11', 'F35-39'
  UNION ALL SELECT 'Nikki Leith', TIMESTAMP '2016-10-18 2:59:01', 'F30-34'
  UNION ALL SELECT 'Lauren Matthews', TIMESTAMP '2016-10-18 3:01:17', 'F35-39'
  UNION ALL SELECT 'Desiree Berry', TIMESTAMP '2016-10-18 3:05:42', 'F35-39'
  UNION ALL SELECT 'Suzy Slane', TIMESTAMP '2016-10-18 3:06:24', 'F35-39'
  UNION ALL SELECT 'Jen Edwards', TIMESTAMP '2016-10-18 3:06:36', 'F30-34'
  UNION ALL SELECT 'Meghan Lederer', TIMESTAMP '2016-10-18 3:07:41', 'F30-34'
  UNION ALL SELECT 'Carly Forte', TIMESTAMP '2016-10-18 3:08:58', 'F25-29'
  UNION ALL SELECT 'Lauren Reasoner', TIMESTAMP '2016-10-18 3:10:14', 'F30-34')
SELECT name,
  finish_time,
  division,
  LEAD(name, 2)
    OVER (PARTITION BY division ORDER BY finish_time ASC) AS two_runners_back
FROM finishers;

/*-----------------+-------------+----------+------------------*
 | name | finish_time | division | two_runners_back |
 +-----------------+-------------+----------+------------------+
 | Carly Forte | 03:08:58 | F25-29 | NULL |
 | Sophia Liu | 02:51:45 | F30-34 | Jen Edwards |
 | Nikki Leith | 02:59:01 | F30-34 | Meghan Lederer |
 | Jen Edwards | 03:06:36 | F30-34 | Lauren Reasoner |
 | Meghan Lederer | 03:07:41 | F30-34 | NULL |
 | Lauren Reasoner | 03:10:14 | F30-34 | NULL |
 | Lisa Stelzner | 02:54:11 | F35-39 | Desiree Berry |
 | Lauren Matthews | 03:01:17 | F35-39 | Suzy Slane |
 | Desiree Berry | 03:05:42 | F35-39 | NULL |
 | Suzy Slane | 03:06:24 | F35-39 | NULL |
 *-----------------+-------------+----------+------------------*/
\`\`\`

The following example replaces NULL values with a default value.



\`\`\`
WITH finishers AS
 (SELECT 'Sophia Liu' as name,
  TIMESTAMP '2016-10-18 2:51:45' as finish_time,
  'F30-34' as division
  UNION ALL SELECT 'Lisa Stelzner', TIMESTAMP '2016-10-18 2:54:11', 'F35-39'
  UNION ALL SELECT 'Nikki Leith', TIMESTAMP '2016-10-18 2:59:01', 'F30-34'
  UNION ALL SELECT 'Lauren Matthews', TIMESTAMP '2016-10-18 3:01:17', 'F35-39'
  UNION ALL SELECT 'Desiree Berry', TIMESTAMP '2016-10-18 3:05:42', 'F35-39'
  UNION ALL SELECT 'Suzy Slane', TIMESTAMP '2016-10-18 3:06:24', 'F35-39'
  UNION ALL SELECT 'Jen Edwards', TIMESTAMP '2016-10-18 3:06:36', 'F30-34'
  UNION ALL SELECT 'Meghan Lederer', TIMESTAMP '2016-10-18 3:07:41', 'F30-34'
  UNION ALL SELECT 'Carly Forte', TIMESTAMP '2016-10-18 3:08:58', 'F25-29'
  UNION ALL SELECT 'Lauren Reasoner', TIMESTAMP '2016-10-18 3:10:14', 'F30-34')
SELECT name,
  finish_time,
  division,
  LEAD(name, 2, 'Nobody')
    OVER (PARTITION BY division ORDER BY finish_time ASC) AS two_runners_back
FROM finishers;

/*-----------------+-------------+----------+------------------*
 | name | finish_time | division | two_runners_back |
 +-----------------+-------------+----------+------------------+
 | Carly Forte | 03:08:58 | F25-29 | Nobody |
 | Sophia Liu | 02:51:45 | F30-34 | Jen Edwards |
 | Nikki Leith | 02:59:01 | F30-34 | Meghan Lederer |
 | Jen Edwards | 03:06:36 | F30-34 | Lauren Reasoner |
 | Meghan Lederer | 03:07:41 | F30-34 | Nobody |
 | Lauren Reasoner | 03:10:14 | F30-34 | Nobody |
 | Lisa Stelzner | 02:54:11 | F35-39 | Desiree Berry |
 | Lauren Matthews | 03:01:17 | F35-39 | Suzy Slane |
 | Desiree Berry | 03:05:42 | F35-39 | Nobody |
 | Suzy Slane | 03:06:24 | F35-39 | Nobody |
 *-----------------+-------------+----------+------------------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/navigation_functions
`;
